/**
 * Тексти підказок «Як користуватись» для кожного екрана CRM. Правило: одна дія на картку, короткі речення,
 * назви кнопок — так, як вони написані на екрані. Порядок важливий: перший збіг шляху виграє, тож конкретні
 * шляхи (деталі, вкладки) — вище за загальні. Останній набір ловить будь-який екран без власних підказок.
 */
export type HelpIconName =
  | 'list' | 'plus' | 'clock' | 'case' | 'hand' | 'chat' | 'check' | 'doc' | 'bug' | 'photo' | 'phone' | 'link' | 'user'
  | 'truck' | 'box' | 'money' | 'chart' | 'gear' | 'globe' | 'calendar' | 'star' | 'shield' | 'search' | 'bell';
export interface HelpSlide { icon: HelpIconName; title: string; text: string }
export interface HelpSet { key: string; name: string; match: (p: string) => boolean; slides: HelpSlide[] }

const is = (...paths: string[]) => (p: string) => paths.includes(p);
const under = (prefix: string) => (p: string) => p.startsWith(`${prefix}/`);

const BUG: HelpSlide = { icon: 'bug', title: 'Щось незрозуміло?', text: 'Натисніть «Є проблема?» внизу праворуч і напишіть своїми словами. Ми допоможемо.' };

export const HELP_SETS: HelpSet[] = [
  // ---------------------------------------------------------------- головні
  {
    key: 'today', name: 'Мій день', match: is('/', '/today'),
    slides: [
      { icon: 'list', title: 'Це ваш стартовий екран', text: 'Тут видно, що треба зробити сьогодні. Починайте день звідси.' },
      { icon: 'bell', title: '«Потребує уваги» — першим', text: 'Тут прострочене і термінове. Розберіть це, а потім усе інше.' },
      { icon: 'check', title: '«Мої задачі»', text: 'Ваші задачі на сьогодні. Зробили — поставте галочку.' },
      { icon: 'list', title: 'Меню ліворуч', text: 'Натисніть потрібний розділ. Цифра поруч — скільки там чекає.' },
    ],
  },
  {
    key: 'control', name: 'Показники й цілі', match: is('/control'),
    slides: [
      { icon: 'chart', title: 'Як іде робота', text: 'Тут головні цифри: скільки заявок, справ і задач, і чи встигаємо ми до цілі.' },
      { icon: 'bell', title: '«Потребує уваги»', text: 'Що затримується. Натисніть рядок, щоб відкрити й розібратися.' },
    ],
  },
  {
    key: 'workspace', name: 'Робочий простір', match: under('/w'),
    slides: [
      { icon: 'list', title: 'Ваш робочий простір', text: 'Тут зібрано все для однієї частини роботи: продажі, справи, транспорт тощо.' },
      { icon: 'search', title: 'Перемкнути простір', text: 'Угорі є список просторів. Виберіть інший, і меню ліворуч зміниться.' },
      { icon: 'clock', title: '«Останні»', text: 'Те, з чим ви працювали нещодавно. Натисніть, щоб швидко повернутись.' },
    ],
  },
  {
    key: 'overview', name: 'Огляд власника', match: is('/overview'),
    slides: [
      { icon: 'chart', title: 'Уся компанія на одному екрані', text: 'По одній головній цифрі з кожного напряму.' },
      { icon: 'hand', title: 'Натисніть плитку', text: 'Відкриється потрібний розділ з деталями.' },
    ],
  },
  {
    key: 'notifications', name: 'Сповіщення', match: is('/notifications'),
    slides: [
      { icon: 'bell', title: 'Усе, що стосується вас', text: 'Нові заявки й задачі, повідомлення клієнтів, нагадування.' },
      { icon: 'hand', title: 'Натисніть сповіщення', text: 'Відкриється те, про що воно: заявка, справа чи задача.' },
      { icon: 'check', title: 'Прочитали все?', text: 'Натисніть «Позначити всі прочитаними», щоб очистити список.' },
    ],
  },

  // ---------------------------------------------------------------- продажі
  {
    key: 'lead', name: 'Заявка', match: under('/leads'),
    slides: [
      { icon: 'user', title: 'Тут усе про одну людину', text: 'Імʼя, телефон, що написала і звідки прийшла заявка.' },
      { icon: 'hand', title: 'Натисніть «Взяти собі»', text: 'Тоді заявка ваша, і колеги не дзвонитимуть тій самій людині.' },
      { icon: 'phone', title: 'Подзвоніть або напишіть', text: 'Після розмови коротко запишіть, про що домовились, щоб колеги теж бачили.' },
      { icon: 'case', title: 'Домовились — «Створити справу»', text: 'Не наш випадок — «Відмова». Реклама чи бот — «Це спам».' },
    ],
  },
  {
    key: 'leads', name: 'Заявки', match: is('/leads'),
    slides: [
      { icon: 'list', title: 'Тут усі нові звернення', text: 'Заявки з сайтів, Telegram і дзвінків зʼявляються тут самі. Найновіші — нагорі.' },
      { icon: 'plus', title: 'Вам подзвонили?', text: 'Натисніть «+ Нова заявка». Впишіть імʼя, телефон і що людині потрібно. Натисніть «Зберегти».' },
      { icon: 'clock', title: 'Червоне — терміново', text: 'Червоний час означає: людина вже чекає довго. Таким дзвоніть першими.' },
      { icon: 'case', title: 'Людина погодилась?', text: 'Натисніть «Створити справу». Далі вся робота по ній — у розділі «Справи».' },
    ],
  },
  {
    key: 'inbox', name: 'Повідомлення', match: (p) => p === '/inbox' || p.startsWith('/inbox/'),
    slides: [
      { icon: 'chat', title: 'Усі переписки в одному місці', text: 'Telegram, Viber, WhatsApp і чат сайту. Не треба відкривати кожен месенджер окремо.' },
      { icon: 'hand', title: 'Відкрийте розмову', text: 'Натисніть на людину ліворуч. Праворуч — уся переписка.' },
      { icon: 'chat', title: 'Відповідайте тут', text: 'Напишіть унизу і натисніть «Надіслати». Людина отримає відповідь у своєму месенджері.' },
      { icon: 'case', title: 'Привʼяжіть до справи', text: '«Привʼязати до справи» — і переписка буде видна в справі цієї людини.' },
    ],
  },
  {
    key: 'signals', name: 'Сигнали з груп', match: is('/signals'),
    slides: [
      { icon: 'chat', title: 'Люди шукають допомогу', text: 'Це пости з Telegram-груп, де хтось шукає перевезення. Їх знаходить програма сама.' },
      { icon: 'hand', title: 'Натисніть «Взяти»', text: 'Тоді сигнал ваш. Колеги бачать, що ви його взяли, і не пишуть тій самій людині.' },
      { icon: 'chat', title: 'Напишіть людині', text: 'Натисніть «Відкрити пост» і напишіть автору в Telegram. Ввічливо, коротко, без реклами.' },
      { icon: 'phone', title: 'Дала телефон?', text: 'Натисніть «Зробити заявку» і впишіть номер. Не наш випадок — «Не наше».' },
    ],
  },
  {
    key: 'parser', name: 'Парсер груп', match: is('/parser'),
    slides: [
      { icon: 'gear', title: 'Як працює пошук у групах', text: 'Кілька Telegram-акаунтів читають групи і знаходять пости, де шукають перевезення.' },
      { icon: 'user', title: '«Акаунти»', text: 'Скільки груп читає кожен акаунт. Зелений — працює, червоний — потрібна допомога.' },
      { icon: 'plus', title: 'Нові групи', text: 'Вставте посилання на групи і натисніть «Розподілити» — програма скаже, на який акаунт їх додати.' },
    ],
  },
  {
    key: 'marketing', name: 'Маркетинг', match: is('/marketing'),
    slides: [
      { icon: 'chart', title: 'Звідки приходять заявки', text: 'Які сайти й реклама дають заявки і скільки з них стають справами.' },
      { icon: 'star', title: 'Кращі кампанії', text: 'Що працює найкраще за 30 днів. Сюди варто вкладати більше.' },
    ],
  },

  // ---------------------------------------------------------------- справи
  {
    key: 'case', name: 'Справа', match: under('/cases'),
    slides: [
      { icon: 'case', title: 'Тут уся справа', text: 'Хто клієнт, звідки й куди, документи, переписка і кроки.' },
      { icon: 'check', title: 'Робіть по кроках', text: 'Поточний крок підсвічено. Зробили — позначте його, і справа піде далі.' },
      { icon: 'doc', title: 'Документи — сюди', text: 'Фото й скани завантажуйте прямо в справу, не тримайте їх лише в телефоні.' },
      { icon: 'user', title: 'Родина бачить кроки', text: 'Клієнт стежить за кроками у своєму кабінеті. Тому позначайте кроки вчасно.' },
      { icon: 'truck', title: 'Везе інша компанія?', text: '«Передати партнеру» — і справа піде до перевізника. Ви бачитимете, як вона рухається.' },
    ],
  },
  {
    key: 'cases', name: 'Справи', match: is('/cases'),
    slides: [
      { icon: 'case', title: 'Одна справа — одне замовлення', text: 'Репатріація, поховання, перевезення чи памʼятник. Усе по людині — в одній справі.' },
      { icon: 'hand', title: 'Відкрийте справу', text: 'Натисніть на рядок, щоб побачити всі деталі й кроки.' },
      { icon: 'plus', title: 'Нова справа', text: 'Зазвичай справа створюється з заявки. Але можна й тут: «Нова справа».' },
    ],
  },
  {
    key: 'waiting', name: 'Чого чекаємо', match: is('/waiting'),
    slides: [
      { icon: 'doc', title: 'Яких документів ще немає', text: 'Список документів по відкритих справах: від кого чекаємо і до коли.' },
      { icon: 'bell', title: 'Нагадайте', text: 'Натисніть «Нагадати», і людина отримає повідомлення.' },
      { icon: 'check', title: 'Отримали документ?', text: 'Натисніть «Отримано», і рядок зникне зі списку.' },
    ],
  },
  {
    key: 'tasks', name: 'Задачі', match: is('/tasks'),
    slides: [
      { icon: 'list', title: 'Ваш список справ на сьогодні', text: 'Усе, що треба зробити, — в одному списку.' },
      { icon: 'clock', title: 'Червона дата — запізнення', text: 'Таку задачу зробіть першою.' },
      { icon: 'check', title: 'Зробили — поставте галочку', text: 'Задача зникне зі списку. Так усі бачать, що вона виконана.' },
    ],
  },
  {
    key: 'planner', name: 'Планування', match: is('/planner'),
    slides: [
      { icon: 'calendar', title: 'Хто коли працює', text: 'Календар команди: чергування, відпустки, зустрічі.' },
      { icon: 'phone', title: 'Черговий', text: 'Хто черговий — тому приходять нові заявки й дзвінки, у яких ще немає відповідального.' },
      { icon: 'plus', title: 'Нова подія', text: 'Натисніть «Нова подія», щоб додати чергування чи відпустку.' },
    ],
  },
  {
    key: 'documents', name: 'Документи', match: is('/documents'),
    slides: [
      { icon: 'doc', title: 'Усі документи компанії', text: 'Договори, довідки, документи справ. Шукайте за назвою чи справою.' },
      { icon: 'plus', title: 'Додати документ', text: 'Натисніть «Завантажити» і виберіть файл з компʼютера чи телефона.' },
    ],
  },
  {
    key: 'myDocuments', name: 'Мої документи', match: is('/my-documents'),
    slides: [
      { icon: 'doc', title: 'Ваші особисті документи', text: 'Посвідчення, довідки, договір. Тут видно, що чинне, а що треба оновити.' },
      { icon: 'bell', title: 'Нагадаємо заздалегідь', text: 'Коли термін документа закінчується, ви отримаєте нагадування.' },
    ],
  },
  {
    key: 'countries', name: 'Країни', match: is('/countries'),
    slides: [
      { icon: 'globe', title: 'Правила кожної країни', text: 'Які документи й дії потрібні, коли тіло везуть звідти, сюди або через цю країну.' },
      { icon: 'list', title: 'Це підказка для справ', text: 'Ці правила самі потрапляють у план справи. Менше шансів щось забути.' },
    ],
  },
  {
    key: 'ritualItem', name: 'Ритуальна послуга', match: under('/ritual'),
    slides: [
      { icon: 'list', title: 'Що пропонуємо родині', text: 'Товари й послуги для цієї родини: труна, атрибутика, церемонія.' },
      { icon: 'check', title: 'Зробили — позначте', text: 'Позначте етап виконаним. Не погодились — «Відмова».' },
    ],
  },
  {
    key: 'ritual', name: 'Ритуальні послуги', match: is('/ritual'),
    slides: [
      { icon: 'star', title: 'Додаткові послуги родині', text: 'Після перевезення в Україну можна запропонувати труну, атрибутику, церемонію, поховання.' },
      { icon: 'plus', title: 'Нова можливість', text: 'Натисніть «Нова можливість», якщо родина зацікавилась.' },
    ],
  },
  {
    key: 'qualityItem', name: 'Звернення якості', match: under('/quality'),
    slides: [
      { icon: 'chat', title: 'Що сталося', text: 'Опис скарги чи відгуку. Нижче — хто що робить і яке рішення.' },
      { icon: 'check', title: 'Запишіть рішення', text: 'Що зробили, щоб виправити. Натисніть «Зберегти».' },
    ],
  },
  {
    key: 'quality', name: 'Якість', match: is('/quality'),
    slides: [
      { icon: 'star', title: 'Скарги й відгуки', text: 'Що кажуть родини, партнери й колеги. Щоб ставати кращими.' },
      { icon: 'plus', title: 'Нове звернення', text: 'Хтось поскаржився чи подякував? Натисніть «Нове звернення» і запишіть.' },
    ],
  },

  // ---------------------------------------------------------------- транспорт
  {
    key: 'handover', name: 'Передача', match: under('/handovers'),
    slides: [
      { icon: 'truck', title: 'Справа від іншої компанії', text: 'Тут видно маршрут, дати і що просить відправник.' },
      { icon: 'check', title: 'Беретесь?', text: 'Натисніть «Прийняти» або «Відхилити».' },
      { icon: 'photo', title: 'Позначайте хід', text: 'Оновлюйте етап і завантажуйте фото — відправник бачить, як іде перевезення.' },
    ],
  },
  {
    key: 'handovers', name: 'Передані нам', match: is('/handovers'),
    slides: [
      { icon: 'truck', title: 'Справи від партнерів', text: 'Інші компанії передали вам ці справи для виконання.' },
      { icon: 'hand', title: 'Відкрийте справу', text: 'Натисніть рядок, щоб прийняти справу і вести її.' },
    ],
  },
  {
    key: 'fleet', name: 'Рейси і місця', match: is('/fleet'),
    slides: [
      { icon: 'truck', title: 'Машини й рейси', text: 'Куди і коли їде кожна машина, скільки місць вільно.' },
      { icon: 'search', title: 'Знайдіть місце', text: 'Виберіть дату й маршрут — побачите, де є вільне місце.' },
    ],
  },
  {
    key: 'vehicleTypes', name: 'Типи машин', match: is('/vehicle-types'),
    slides: [
      { icon: 'truck', title: 'Які бувають машини', text: 'Типи машин і скільки місць у кожній. Це потрібно, щоб правильно рахувати рейси.' },
    ],
  },
  {
    key: 'freight', name: 'Вантажні рейси', match: is('/freight'),
    slides: [
      { icon: 'truck', title: 'Великі перевезення трун', text: 'План кузова, партії з виробництва і графік рейсів.' },
    ],
  },

  // ---------------------------------------------------------------- торгівля
  {
    key: 'orderNew', name: 'Нове замовлення', match: is('/orders/new'),
    slides: [
      { icon: 'box', title: 'Додайте товари', text: 'Знайдіть товар і вкажіть кількість.' },
      { icon: 'user', title: 'Хто покупець', text: 'Знайдіть клієнта за телефоном або натисніть «Новий клієнт».' },
      { icon: 'truck', title: 'Доставка', text: 'Куди і як везти. Потім натисніть «Створити».' },
    ],
  },
  {
    key: 'order', name: 'Замовлення', match: under('/orders'),
    slides: [
      { icon: 'box', title: 'Усе про замовлення', text: 'Товари, оплата, доставка і нотатки.' },
      { icon: 'check', title: 'Ведіть по кнопках', text: '«Підтвердити» → «Відвантажити» → «Завершити». Кожна кнопка — наступний крок.' },
      { icon: 'doc', title: 'Рахунок', text: '«Рахунок PDF» — готовий рахунок для клієнта.' },
    ],
  },
  {
    key: 'orders', name: 'Замовлення', match: is('/orders'),
    slides: [
      { icon: 'box', title: 'Усі замовлення товарів', text: 'З сайту, Prom, телефону й від оптових клієнтів.' },
      { icon: 'plus', title: 'Замовили по телефону?', text: 'Натисніть «Нове замовлення».' },
      { icon: 'bell', title: 'Позначка «не вистачило»', text: 'Товару немає на складі. Перевірте склад і підтвердіть вручну.' },
    ],
  },
  {
    key: 'b2b', name: 'Оптові клієнти', match: is('/b2b'),
    slides: [
      { icon: 'user', title: 'Ритуальні агенції', text: 'Оптові клієнти: їхні ціни, ліміт і відстрочка оплати.' },
      { icon: 'money', title: 'Баланс', text: 'Скільки клієнт винен і скільки ще може взяти в борг.' },
    ],
  },
  {
    key: 'productEdit', name: 'Редагування товару', match: (p) => p === '/products/new' || /^\/products\/[^/]+\/edit$/.test(p),
    slides: [
      { icon: 'box', title: 'Заповніть «Основне»', text: 'Назва, категорія, фото. Цього вже достатньо, щоб зберегти товар.' },
      { icon: 'globe', title: 'Переклади', text: 'Назва й опис іншими мовами для сайтів у Молдові й Польщі.' },
      { icon: 'list', title: 'Варіанти', text: 'Якщо товар буває різних розмірів чи кольорів — додайте їх тут.' },
    ],
  },
  {
    key: 'product', name: 'Товар', match: under('/products'),
    slides: [
      { icon: 'box', title: 'Картка товару', text: 'Фото, ціна, варіанти і скільки є на складі.' },
      { icon: 'gear', title: 'Змінити', text: 'Натисніть «Редагувати», щоб поміняти опис, фото чи ціну.' },
    ],
  },
  {
    key: 'catalog', name: 'Каталог', match: is('/products'),
    slides: [
      { icon: 'box', title: 'Усі товари', text: 'Труни, урни, памʼятники, послуги. Шукайте за назвою.' },
      { icon: 'plus', title: 'Новий товар', text: 'Натисніть «Створити» і заповніть назву, категорію і фото.' },
    ],
  },
  {
    key: 'collections', name: 'Колекції', match: is('/collections'),
    slides: [
      { icon: 'star', title: 'Нові лінійки товарів', text: 'План продажів на перші 30 і 90 днів і як він виконується.' },
    ],
  },
  {
    key: 'pricing', name: 'Ціни', match: is('/pricing'),
    slides: [
      { icon: 'money', title: 'Ціни й знижки', text: 'Прайс-листи, знижки, курси НБУ і націнки.' },
      { icon: 'plus', title: 'Новий прайс-лист', text: 'Наприклад, окремі ціни для оптових клієнтів.' },
    ],
  },
  {
    key: 'inventory', name: 'Склад', match: is('/inventory'),
    slides: [
      { icon: 'box', title: 'Скільки товару де лежить', text: 'Залишки на кожному складі.' },
      { icon: 'truck', title: 'Прийшов товар або перевезли?', text: 'Запишіть надходження чи переміщення, щоб залишки були правильні.' },
    ],
  },
  {
    key: 'channels', name: 'Канали продажу', match: is('/channels'),
    slides: [
      { icon: 'link', title: 'Маркетплейси', text: 'Посилання для Prom та інших: товари туди підтягуються самі.' },
    ],
  },
  {
    key: 'money', name: 'Гроші', match: (p) => p === '/money' || p.startsWith('/money/'),
    slides: [
      { icon: 'money', title: 'Платежі й рахунки', text: 'Скільки надійшло і що ще мають оплатити.' },
      { icon: 'plus', title: 'Отримали оплату?', text: 'Натисніть «Додати платіж».' },
      { icon: 'doc', title: 'Потрібен рахунок?', text: 'Натисніть «Новий рахунок».' },
    ],
  },
  {
    key: 'processes', name: 'Процеси', match: is('/processes'),
    slides: [
      { icon: 'gear', title: 'Шаблони кроків', text: 'Які кроки проходить справа для кожної країни.' },
      { icon: 'shield', title: 'Обережно', text: 'Зміни тут впливають на всі нові справи. Не впевнені — спитайте керівника.' },
    ],
  },
  {
    key: 'dictionaries', name: 'Властивості товарів', match: is('/dictionaries'),
    slides: [
      { icon: 'list', title: 'Кольори, матеріали, розміри', text: 'Нове значення — просто новий рядок. Потім його можна вибрати в товарі.' },
    ],
  },
  {
    key: 'reports', name: 'Звіти про проблеми', match: is('/reports'),
    slides: [
      { icon: 'bug', title: 'Щось не працює?', text: 'На будь-якому екрані натисніть «Є проблема?» внизу праворуч.' },
      { icon: 'chat', title: 'Напишіть, що сталося', text: 'Що натискали і що побачили. Своїми словами — цього достатньо.' },
      { icon: 'photo', title: 'Додайте картинку', text: 'Натисніть «Зняти екран» або вставте знімок кнопками Ctrl+V.' },
      { icon: 'check', title: 'Тут видно, що з вашим звітом', text: '«Новий» — ще не дивились. «У роботі» — виправляємо. «Виправлено» — готово.' },
    ],
  },

  // ---------------------------------------------------------------- налаштування (вкладки)
  {
    key: 'tgbot', name: 'Telegram-бот', match: is('/settings/tgbot'),
    slides: [
      { icon: 'link', title: 'Підключіть свій Telegram', text: 'Виберіть людину і створіть код. Відкрийте бота @viadomum_bot, натисніть Start і надішліть код.' },
      { icon: 'hand', title: 'Заявки приходять у групу', text: 'Нова заявка зʼявляється в темі «Заявки». Натисніть «Взяти» — і вона ваша.' },
      { icon: 'phone', title: 'CRM у телефоні', text: 'У чаті з ботом натисніть кнопку CRM унизу — відкриються ваші заявки й справи.' },
    ],
  },
  {
    key: 'sites', name: 'Сайти', match: is('/settings/sites'),
    slides: [
      { icon: 'globe', title: 'Наші сайти', text: 'Які сайти приймають заявки і на якій мові.' },
      { icon: 'shield', title: 'Змінюйте обережно', text: 'Від цих налаштувань залежить, куди йдуть заявки з сайтів.' },
    ],
  },
  {
    key: 'duty', name: 'Чергування', match: is('/settings/duty'),
    slides: [
      { icon: 'phone', title: 'Хто черговий', text: 'Черговому приходять нові заявки, у яких ще немає відповідального.' },
    ],
  },
  {
    key: 'audit', name: 'Журнал дій', match: is('/settings/audit'),
    slides: [
      { icon: 'search', title: 'Хто що змінив', text: 'Усі важливі дії в CRM: хто, що і коли. Допомагає розібратись, якщо щось пропало.' },
    ],
  },
  {
    key: 'security', name: 'Безпека', match: is('/settings/security'),
    slides: [
      { icon: 'shield', title: 'Ваш пароль', text: 'Тут можна змінити свій пароль. Не кажіть його нікому.' },
      { icon: 'phone', title: 'Додатковий захист', text: 'Увімкніть код з телефона при вході — тоді ніхто не зайде навіть з вашим паролем.' },
    ],
  },
  {
    key: 'partners', name: 'Партнерські компанії', match: is('/settings/partners'),
    slides: [
      { icon: 'truck', title: 'Перевізники-партнери', text: 'Компанії, яким ви передаєте справи. У кожної — свій вхід.' },
    ],
  },
  {
    key: 'workspaces', name: 'Робочі простори', match: is('/settings/workspaces'),
    slides: [
      { icon: 'user', title: 'Хто де працює', text: 'Позначте, які робочі простори бачить кожна людина.' },
    ],
  },
  {
    key: 'languages', name: 'Мови', match: is('/settings/languages'),
    slides: [
      { icon: 'globe', title: 'Переклади інтерфейсу', text: 'Тексти кабінетів і документів іншими мовами. Змінюйте лише те, що точно знаєте.' },
    ],
  },
  {
    key: 'import', name: 'Імпорт', match: is('/settings/import'),
    slides: [
      { icon: 'doc', title: 'Перенести старі дані', text: 'Завантажте таблицю зі справами чи заявками за шаблоном. Спершу перевірте, потім імпортуйте.' },
    ],
  },
  {
    key: 'users', name: 'Користувачі', match: (p) => p === '/settings' || p === '/settings/users',
    slides: [
      { icon: 'user', title: 'Тут ваша команда', text: 'Усі, хто має вхід у CRM, і що кожен може робити.' },
      { icon: 'plus', title: 'Новий працівник?', text: 'Додайте людину: імʼя, email для входу, роль і тимчасовий пароль. Передайте пароль особисто.' },
      { icon: 'check', title: 'Пішов з роботи?', text: 'Вимкніть доступ, щоб людина більше не могла зайти.' },
    ],
  },

  // будь-який інший екран
  {
    key: 'other', name: 'Цей екран', match: () => true,
    slides: [
      { icon: 'list', title: 'Меню ліворуч', text: 'Натисніть потрібний розділ, щоб перейти до нього.' },
      { icon: 'search', title: 'Пошук угорі', text: 'Шукайте людину, справу чи заявку за телефоном, імʼям або номером.' },
      BUG,
    ],
  },
];
